import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { gameAPI } from '../services/api';
import { formatNumber } from '../utils/formatters';
import './TasksPage.css';

interface Task {
  id: number;
  name: string;
  description: string;
  reward: number;
  status: 'pending' | 'in_progress' | 'completed';
  category: 'daily' | 'social' | 'achievements';
  action_url?: string;
  check_type?: string;
  check_data?: string;
  progress?: number;
  max_progress?: number;
  required_count?: number;
  completed_at?: string;
}

const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<{
    daily: Task[];
    social: Task[];
    achievements: Task[];
  }>({
    daily: [],
    social: [],
    achievements: []
  });
  const [activeTab, setActiveTab] = useState<'daily' | 'social' | 'achievements'>('daily');
  const [loading, setLoading] = useState(true);
  const [totalRewards, setTotalRewards] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tasks/list');
      
      if (response.data.success) {
        setTasks(response.data.data);
        
        // Подсчитываем статистику
        let rewards = 0;
        let completed = 0;
        
        Object.values(response.data.data).forEach((categoryTasks: any) => {
          categoryTasks.forEach((task: Task) => {
            if (task.status === 'completed') {
              rewards += task.reward;
              completed++;
            }
          });
        });
        
        setTotalRewards(rewards);
        setCompletedCount(completed);
      }
    } catch (error) {
      console.error('Ошибка загрузки заданий:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = async (task: Task) => {
    // Если задание уже выполнено
    if (task.status === 'completed') {
      return;
    }

    // Если это социальное задание с ссылкой
    if (task.action_url) {
      window.open(task.action_url, '_blank');
      
      // Отмечаем задание как начатое
      if (task.status === 'pending') {
        await startTask(task.id);
      }
      
      // Через 3 секунды проверяем выполнение
      setTimeout(() => {
        checkTask(task.id);
      }, 3000);
    } else {
      // Для других заданий сразу проверяем выполнение
      await checkTask(task.id);
    }
  };

  const startTask = async (taskId: number) => {
    try {
      await api.post('/tasks/start', { taskId });
      await fetchTasks(); // Обновляем список
    } catch (error) {
      console.error('Ошибка начала задания:', error);
    }
  };

  const checkTask = async (taskId: number) => {
    try {
      const response = await api.post('/tasks/check', { taskId });
      
      if (response.data.success) {
        if (response.data.data.completed) {
          showNotification(`🎉 Задание выполнено! +${response.data.data.reward} SPERM`);
        } else if (response.data.data.progress) {
          showNotification(`📈 Прогресс: ${response.data.data.progress}/${response.data.data.max_progress}`);
        }
        await fetchTasks(); // Обновляем список
      }
    } catch (error) {
      console.error('Ошибка проверки задания:', error);
    }
  };

  const completeTask = async (taskId: number) => {
    try {
      const response = await api.post('/tasks/complete', { taskId });
      
      if (response.data.success) {
        showNotification(`✅ Получена награда: +${response.data.data.reward} SPERM`);
        await fetchTasks(); // Обновляем список
      }
    } catch (error) {
      console.error('Ошибка завершения задания:', error);
    }
  };

  const showNotification = (message: string) => {
    // Здесь можно добавить красивое уведомление
    console.log(message);
  };

  const getTaskIcon = (task: Task) => {
    if (task.category === 'daily') {
      if (task.name.includes('вход')) return '🎯';
      if (task.name.includes('монеты')) return '💰';
      if (task.name.includes('игру')) return '🎮';
      if (task.name.includes('друга')) return '👥';
      return '📋';
    }
    if (task.category === 'social') {
      if (task.name.includes('Telegram')) return '📢';
      if (task.name.includes('Twitter')) return '🐦';
      if (task.name.includes('YouTube')) return '📺';
      if (task.name.includes('Instagram')) return '📷';
      return '🔗';
    }
    if (task.category === 'achievements') {
      if (task.name.includes('стейкинг')) return '🚀';
      if (task.name.includes('Накопитель')) return '💎';
      if (task.name.includes('босс')) return '👑';
      if (task.name.includes('Игроман')) return '🎮';
      if (task.name.includes('VIP')) return '⭐';
      if (task.name.includes('Регулярность')) return '📅';
      return '🏆';
    }
    return '📋';
  };

  const getTabStats = (category: 'daily' | 'social' | 'achievements') => {
    const categoryTasks = tasks[category];
    const completed = categoryTasks.filter(t => t.status === 'completed').length;
    const total = categoryTasks.length;
    return { completed, total };
  };

  if (loading) {
    return (
      <div className="tasks-page">
        <div className="loading-spinner">Загрузка заданий...</div>
      </div>
    );
  }

  return (
    <div className="tasks-page">
      <div className="page-container">
        <div className="page-header">
          <button onClick={() => navigate(-1)} className="back-button">
            ← Назад
          </button>
          <h1 className="page-title">📋 Задания</h1>
        </div>

        {/* Статистика */}
        <div className="tasks-stats">
          <div className="stat-card">
            <span className="stat-icon">✅</span>
            <div className="stat-info">
              <span className="stat-value">{completedCount}</span>
              <span className="stat-label">Выполнено</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">💰</span>
            <div className="stat-info">
              <span className="stat-value">{formatNumber(totalRewards, 0)}</span>
              <span className="stat-label">Заработано</span>
            </div>
          </div>
        </div>

        {/* Табы категорий */}
        <div className="tasks-tabs">
          <button
            className={`task-tab ${activeTab === 'daily' ? 'active' : ''}`}
            onClick={() => setActiveTab('daily')}
          >
            <span className="tab-icon">📅</span>
            <span className="tab-text">Ежедневные</span>
            <span className="tab-badge">
              {getTabStats('daily').completed}/{getTabStats('daily').total}
            </span>
          </button>
          <button
            className={`task-tab ${activeTab === 'social' ? 'active' : ''}`}
            onClick={() => setActiveTab('social')}
          >
            <span className="tab-icon">🔗</span>
            <span className="tab-text">Социальные</span>
            <span className="tab-badge">
              {getTabStats('social').completed}/{getTabStats('social').total}
            </span>
          </button>
          <button
            className={`task-tab ${activeTab === 'achievements' ? 'active' : ''}`}
            onClick={() => setActiveTab('achievements')}
          >
            <span className="tab-icon">🏆</span>
            <span className="tab-text">Достижения</span>
            <span className="tab-badge">
              {getTabStats('achievements').completed}/{getTabStats('achievements').total}
            </span>
          </button>
        </div>

        {/* Список заданий */}
        <div className="tasks-list">
          {tasks[activeTab].map(task => (
            <div
              key={task.id}
              className={`task-card ${task.status}`}
              onClick={() => handleTaskClick(task)}
            >
              <div className="task-icon">{getTaskIcon(task)}</div>
              <div className="task-content">
                <h3 className="task-title">{task.name}</h3>
                <p className="task-description">{task.description}</p>
                {task.progress !== undefined && task.max_progress && (
                  <div className="task-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${(task.progress / task.max_progress) * 100}%` }}
                      />
                    </div>
                    <span className="progress-text">
                      {task.progress} / {task.max_progress}
                    </span>
                  </div>
                )}
              </div>
              <div className="task-reward">
                <span className="reward-amount">+{formatNumber(task.reward, 0)}</span>
                <span className="reward-label">SPERM</span>
              </div>
              <div className="task-status">
                {task.status === 'completed' ? (
                  <span className="status-completed">✅</span>
                ) : task.status === 'in_progress' ? (
                  <span className="status-progress">⏳</span>
                ) : (
                  <span className="status-pending">▶️</span>
                )}
              </div>
            </div>
          ))}
          
          {tasks[activeTab].length === 0 && (
            <div className="no-tasks">
              <span className="no-tasks-icon">📭</span>
              <p>Нет заданий в этой категории</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TasksPage;